<template>
    <div
        id="theme-setting-modal"
        :class="[{ 'modal fade flex items-center p-2 md:p-0': true, show: showModal, close: !showModal }]"
    >
        <div @click="closeModal()" class="bg-backdrop absolute w-full h-full"></div>
        <div class="modal-content w-full h-full flex flex-col">
            <div class="modal-header flex justify-between items-center p-4">
                <h3 class="m-0 shadow-text-none">{{ $t('setting_modal_header_theme_settings') }}</h3>
                <div @click="closeModal" class="close-btn-box flex justify-center items-center">
                    <i class="fas fa-times text-xl shadow-text-none cursor-pointer"></i>
                </div>
            </div>
            <div class="modal-body w-full h-full p-4 pb-16 overflow-y-auto">
                <!-- themes select -->
                <h3 class="m-0 shadow-text-none">{{ $t('setting_modal_label_theme') }}</h3>
                <div class="flex flex-wrap mt-4" style="gap: 20px">
                    <div v-for="theme in themes" :key="theme.name">
                        <ThemeSelect
                            @select="onSelectTheme(theme.name)"
                            @remove="onRemoveTheme"
                            :active="themeName == theme.name"
                            :theme="theme"
                        />
                    </div>
                    <div v-if="themesCount < 8" @click="addNewTheme" class="new-theme">
                        <div class="text-add-new">{{ $t('setting_modal_theme_add_new') }}</div>
                    </div>
                </div>

                <ColorContrastWarning
                    @change-button-and-text-color="onChangeButtonAndTextColor($event, themes[themeName])"
                    @change-background-and-text-color="onChangeBackgrounAndTextColor($event, themes[themeName])"
                    @change-background-and-button-color="onChangeBackgrounAndButtonColor($event, themes[themeName])"
                    :background-color="themes[themeName].background_color"
                    :text-color="themes[themeName].question_text_color"
                    :button-color="themes[themeName].button_color"
                    :button-text-color="themes[themeName].button_text_color"
                />

                <div v-if="themeName" class="theme-section">
                    <hr class="mt-5 mb-4 border-gray border-t-0 border-l-0 border-r-0" />
                    <div class="flex flex-wrap items-center">
                        <div class="w-32 mb-3 md:mb-0">
                            <h3 class="m-0 shadow-text-none">{{ $t('setting_modal_theme_question') }}</h3>
                        </div>
                        <div class="w-auto flex">
                            <div class="flex justify-center items-center pr-6">
                                <Swatches
                                    v-model="themes[themeName].question_text_color"
                                    :trigger-style="{
                                        width: '35px',
                                        height: '35px',
                                        border: '2px solid #ccc'
                                    }"
                                    shapes="circles"
                                    colors="text-advanced"
                                    show-fallback
                                />
                                <span class="pl-2 shadow-text-none">{{ $t('setting_modal_theme_text') }}</span>
                            </div>
                            <div class="flex justify-center items-center">
                                <Swatches
                                    v-model="themes[themeName].question_shadow_color"
                                    :trigger-style="{
                                        width: '35px',
                                        height: '35px',
                                        border: '2px solid #ccc'
                                    }"
                                    :swatch-style="{
                                        borderRadius: '0',
                                        padding: '0',
                                        margin: '0'
                                    }"
                                    :wrapper-style="{
                                        padding: '0'
                                    }"
                                    :colors="swatches"
                                    popover-to="center"
                                    shapes="circles"
                                    row-length="8"
                                    swatch-size="24"
                                    show-fallback
                                />
                                <span class="pl-2 shadow-text-none">{{ $t('setting_modal_theme_shadow') }}</span>
                            </div>
                        </div>
                    </div>
                    <hr class="my-4 border-gray border-t-0 border-l-0 border-r-0" />
                    <div class="flex flex-wrap items-center">
                        <div class="w-32 mb-3 md:mb-0">
                            <h3 class="m-0 shadow-text-none">{{ $t('setting_modal_theme_button') }}</h3>
                        </div>
                        <div class="w-auto flex items-center">
                            <div class="flex justify-center items-center pr-2">
                                <Swatches
                                    v-model="themes[themeName].button_color"
                                    :trigger-style="{
                                        width: '35px',
                                        height: '35px',
                                        border: '2px solid #ccc'
                                    }"
                                    shapes="circles"
                                    colors="text-advanced"
                                    show-fallback
                                />
                                <span class="pl-2 shadow-text-none">{{ $t('setting_modal_theme_button') }}</span>
                            </div>
                            <div class="flex justify-center items-center">
                                <Swatches
                                    v-model="themes[themeName].button_text_color"
                                    :trigger-style="{
                                        width: '35px',
                                        height: '35px',
                                        border: '2px solid #ccc'
                                    }"
                                    shapes="circles"
                                    colors="text-advanced"
                                    show-fallback
                                />
                                <span class="pl-2 shadow-text-none">{{ $t('setting_modal_theme_text') }}</span>
                            </div>
                        </div>
                    </div>
                    <hr class="my-4 border-gray border-t-0 border-l-0 border-r-0" />
                    <div class="flex flex-wrap items-center">
                        <div class="w-32 mb-3 md:mb-0">
                            <h3 class="m-0 shadow-text-none">{{ $t('setting_modal_theme_background') }}</h3>
                        </div>
                        <div class="w-auto flex">
                            <div class="flex justify-center items-center pr-5">
                                <Swatches
                                    v-model="themes[themeName].background_color"
                                    :trigger-style="{
                                        width: '35px',
                                        height: '35px',
                                        border: '2px solid #ccc'
                                    }"
                                    shapes="circles"
                                    colors="text-advanced"
                                    show-fallback
                                />
                                <span class="pl-2 shadow-text-none">{{ $t('setting_modal_theme_color') }}</span>
                            </div>
                            <div class="flex justify-center items-center">
                                <ThemeImagePicker
                                    @remove="removeThemeImage()"
                                    :class="{ 'cursor-pointer': true, 'pointer-events-none': !canEditQuiz }"
                                    :already-show="true"
                                    :image="themes[themeName].background_image"
                                    :image-original="themes[themeName].background_image_original"
                                    :image-source="themes[themeName].background_image_source"
                                    :image-coordinates="themes[themeName].background_image_coordinates"
                                    :border-color="'#ccc'"
                                    :darken="darken"
                                    :opacity="opacity"
                                    :blur="blur"
                                    ref="themeImagePickerRef"
                                    location="themebgimage"
                                />
                                <span class="pl-2 shadow-text-none">{{ $t('setting_modal_theme_image') }}</span>
                            </div>
                        </div>
                    </div>

                    <template v-if="themes[themeName].background_image">
                        <div class="mt-4">
                            <p class="font-size-14 text-center shadow-text-none">{{ $t('setting_blur') }}</p>
                            <SliderBar
                                @change="updateBackgroundImageBlur($event)"
                                :class="{ 'mt-8': true, 'pointer-events-none': !canEditQuiz }"
                                :value="blur"
                                :min="0"
                                :max="10"
                                :dotSize="30"
                                color="#000"
                            ></SliderBar>
                        </div>
                        <div class="mt-4">
                            <p class="font-size-14 text-center shadow-text-none">{{ $t('setting_darken') }}</p>
                            <SliderBar
                                @change="updateBackgroundImageOpacity($event)"
                                :class="{ 'mt-8': true, 'pointer-events-none': !canEditQuiz }"
                                :value="opacity"
                                :min="0"
                                :max="100"
                                :dotSize="30"
                                color="#000"
                            ></SliderBar>
                        </div>
                    </template>
                    <hr class="my-4 border-gray border-t-0 border-l-0 border-r-0" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import quizCreator from '@/mixins/quiz-creator.js'
import popup from '@/lib/popup.js'
import ThemeImagePicker from '@/components/quiz-creator/ThemeImagePicker'
import Swatches from 'vue-swatches'
import SliderBar from '@/components/SliderBar.vue'
import ThemeSelect from '@/components/quiz-creator/quiz-section/ThemeSelect'
import ColorContrastWarning from '@/components/quiz-creator/quiz-section/ColorContrastWarning'
import IntroHint from '@/lib/intro.js'
import introJsData from '@/mixins/introjs-data'
import { defaultThemes } from '@/store/util'

export default {
    components: {
        Swatches,
        SliderBar,
        ThemeSelect,
        ThemeImagePicker,
        ColorContrastWarning
    },

    mixins: [quizCreator, introJsData],

    data() {
        return {
            showModal: true,
            backgroundTypeText: null,
            bgOptions: [
                {
                    text: 'Color',
                    value: 'color'
                },
                {
                    text: 'Background image',
                    value: 'image'
                }
            ],
            colors: [
                '#ef0000',
                '#1fbc9c',
                '#1ca085',
                '#2ecc70',
                '#27af60',
                '#3398db',
                '#2980b9',
                '#a463bf',
                '#8e43ad',
                '#3d556e',
                '#222f3d',
                '#f2c511',
                '#f39c19',
                '#e84b3c',
                '#c0382b'
            ],
            introHint: new IntroHint()
        }
    },

    created() {
        this.introHint.removeHints()
        setTimeout(() => {
            this.introHint.register(this.hintsThemeSettings)
        }, 500)

        this.updateBackgroundType(this.backgroundType ? this.backgroundType : 'color')
    },

    computed: {
        ...mapState({
            backgroundType: state => state.question.background_type,
            backgroundColor: state => state.question.background_color,
            backgroundImage: state => state.question.background_image,
            backgroundImageOriginal: state => state.question.background_image_original,
            backgroundImageSource: state => state.question.background_image_source,
            backgroundImageCoordinate: state => state.question.background_image_coordinates,
            blur: state => state.question.background_image_blur || 0,
            darken: state => state.question.background_image_darken || 0,
            opacity: state => state.question.background_image_opacity || 0,
            backgroundMusic: state => state.question.background_music,
            isCreateNewQuiz: state => state.isCreateNewQuiz,
            themeName: state => state.question.theme_name,
            themes: state => state.question.themes,
            isShowPaymentPopup: state => state.isShowPaymentPopup,
            themeSystemSettings: state => state.themeSystemSettings
        }),

        ...mapGetters({
            paymentModalTitle: 'paymentModalTitle',
            paymentModalDescription: 'paymentModalDescription'
        }),

        themesCount() {
            return Object.keys(this.themes).length
        },

        swatcherTriggerStyle() {
            return {
                width: '35px',
                height: '35px',
                border: '2px solid #ccc'
            }
        }
    },

    methods: {
        closeModal() {
            this.introHint.removeHints()
            this.introHint.register(this.hintsQuestionList)

            if (this.backgroundType == 'image' && !this.backgroundImage) {
                popup.alert(this.$t('setting_alert_background_image_required'))
                return false
            }

            this.showModal = false

            setTimeout(() => {
                this.$emit('close')
            }, 300)
        },

        updateBackgroundType(type) {
            if (typeof type == 'string') {
                if (type == 'color') {
                    this.backgroundTypeText = this.bgOptions[0].text
                    this.removeBackgroundImage()
                }

                if (type == 'image') {
                    this.backgroundTypeText = this.bgOptions[1].text
                }

                this.$store.commit('updateBackgroundType', type)
            } else {
                this.backgroundTypeText = type.text
                this.$store.commit('updateBackgroundType', type.value)
            }
        },

        updateBackgroundColor(color) {
            if (!this.canEditQuiz) return

            this.$store.commit('updateBackgroundColor', color)
        },

        updateBackgroundImageBlur(blur) {
            if (!this.canEditQuiz) return

            this.$store.commit('updateBackgroundImageBlur', blur)
        },

        updateBackgroundImageDarken(darken) {
            if (!this.canEditQuiz) return

            this.$store.commit('updateBackgroundImageDarken', darken)
        },

        updateBackgroundImageOpacity(opacity) {
            if (!this.canEditQuiz) return

            this.$store.commit('updateBackgroundImageOpacity', opacity)
        },

        onSelectTheme(name) {
            this.$store.commit('setThemeName', name)
        },

        addNewTheme() {
            const keys = Object.keys(this.themes)
            const lastThemeKey = keys[keys.length - 1]
            const lastThemeNumber = Number(lastThemeKey.split('_')[1])
            const newThemeNumber = lastThemeNumber + 1
            const newThemeKey = `theme_${newThemeNumber}`

            const defaultTheme = { ...defaultThemes().theme_1, ...this.themeSystemSettings }
            delete defaultTheme.is_system
            defaultTheme.name = newThemeKey
            const updatedThemes = { ...this.themes, [newThemeKey]: defaultTheme }
            this.$store.commit('setThemes', updatedThemes)
        },

        onRemoveTheme(theme) {
            delete this.themes[theme.name]
            const updatedThemes = { ...this.themes }
            this.$store.commit('setThemes', updatedThemes)
        },

        onChangeButtonAndTextColor({ buttonColor, buttonTextColor }, theme) {
            theme['button_color'] = buttonColor
            theme['button_text_color'] = buttonTextColor
        },

        onChangeBackgrounAndTextColor({ backgroundColor, textColor }, theme) {
            theme['background_color'] = backgroundColor
            theme['question_text_color'] = textColor
        },

        onChangeBackgrounAndButtonColor({ backgroundColor, buttonColor }, theme) {
            theme['background_color'] = backgroundColor
            theme['button_color'] = buttonColor
        }
    }
}
</script>

<style lang="scss">
#theme-setting-modal {
    .modal-content {
        .modal-body {
            .x-select {
                .vs__selected {
                    color: #000;
                    text-shadow: none;
                }
            }

            .theme-section {
                .vue-swatches__container {
                    @media screen and (max-width: 768px) {
                        left: -30% !important;
                        transform: translate(-50%, 0%);
                    }
                }

                .vue-swatches__fallback__wrapper {
                    margin-left: 0 !important;
                    padding-bottom: 0 !important;
                    padding-top: 6px;
                }
            }

            .new-theme {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 66px;
                height: 66px;
                border-radius: 50%;
                border: 3px dashed #ccc;
                cursor: pointer;

                .text-add-new {
                    text-align: center;
                    color: #ccc;
                    font-size: 14px;
                    word-spacing: 4px;
                }
            }
        }
    }
}
</style>
